import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineMagnifyingGlass, HiOutlineTicket, HiArrowRight } from "react-icons/hi2";

const MainPage = () => {
  const [query, setQuery] = useState("");
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forceRender, setForceRender] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    console.log("📌 selectedTicket actualizado:", selectedTicket);
  }, [selectedTicket]);

  const fetchTickets = async (ticketNumber) => {
    setLoading(true);
    try {
      const response = await fetch(`https://desarrollotecnologicoar.com/api8/search-ticket/${ticketNumber}`);
      if (!response.ok) throw new Error("No se encontraron tickets");
      const data = await response.json();
      setFilteredTickets(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setFilteredTickets([]);
      setError("No se encontraron tickets");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setError("");
    if (value.length > 3) {
      fetchTickets(value);
    } else {
      setFilteredTickets([]);
    }
  };

  const limpiarFormulario = () => {
    setFilteredTickets([]);
  };

  const handleSelectTicket = (ticket) => {
    setQuery(ticket.ticketNumber);
    setSelectedTicket(ticket);
    setForceRender((prev) => !prev);
    limpiarFormulario();
  };

  const handleSearch = async () => {
    if (!query) {
      setError("Ingresa un número de ticket");
      return;
    }
    await fetchTickets(query);
    if (filteredTickets.length === 1) {
      navigate(`/asignar_servicio/${filteredTickets[0].id}`);
    }
  };

  const handleGo = () => {
    if (selectedTicket) {
      navigate(`/asignar_servicio/${selectedTicket.id}`);
    } else {
      handleSearch();
    }
  };

  return (
    <div className="page-container flex flex-col items-center justify-center min-h-[calc(100vh-3.5rem)] lg:min-h-screen">
      {/* Header section */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center mb-6">
          <img
            src="/logo_drago.png"
            alt="Dragon Logo"
            className="w-[28rem] h-auto drop-shadow-2xl"
            style={{ animation: "pulseScale 2s ease-in-out infinite" }}
          />
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Buscar Ticket</h1>
        <p className="text-slate-500 text-lg">Ingresa el número de ticket para asignar un servicio</p>
      </div>

      {/* Search box */}
      <div className="w-full max-w-xl relative">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <HiOutlineMagnifyingGlass className="w-5 h-5 text-slate-400" />
          </div>
          <input
            type="text"
            placeholder="Número de ticket..."
            value={query}
            onChange={handleInputChange}
            onKeyDown={(e) => e.key === "Enter" && handleGo()}
            className="w-full pl-12 pr-32 py-4 text-base bg-white border border-slate-300 rounded-xl
                       placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400
                       shadow-sm transition-all duration-150"
          />
          <div className="absolute inset-y-0 right-0 pr-2 flex items-center">
            <button
              onClick={handleGo}
              disabled={loading}
              className="btn-primary px-5 py-2.5 rounded-lg"
            >
              {loading ? (
                <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <>
                  Buscar
                  <HiArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Dropdown results */}
        {filteredTickets.length > 0 && (
          <div className="absolute z-20 mt-2 w-full bg-white rounded-xl border border-slate-200 shadow-lg max-h-64 overflow-y-auto">
            {filteredTickets.map((ticket) => (
              <button
                key={ticket.ticketNumber}
                onClick={() => handleSelectTicket(ticket)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0 first:rounded-t-xl last:rounded-b-xl"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      #{ticket.ticketNumber}
                      <span className="font-normal text-slate-600 ml-2">{ticket.subject}</span>
                    </p>
                  </div>
                  <span className={`badge ${
                    ticket.status === "Open" ? "badge-green" :
                    ticket.status === "Closed" ? "badge-gray" : "badge-yellow"
                  }`}>
                    {ticket.status}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <p className="mt-3 text-sm text-red-600 text-center">{error}</p>
        )}
      </div>

      {/* Selected ticket card */}
      {selectedTicket && Object.keys(selectedTicket).length > 0 && (
        <div className="w-full max-w-xl mt-6 card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Ticket Seleccionado</h3>
            <span className="badge badge-blue">#{selectedTicket.ticketNumber}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-slate-500">Asunto</p>
              <p className="font-medium text-slate-800">{selectedTicket.subject || "No disponible"}</p>
            </div>
            <div>
              <p className="text-slate-500">Estado</p>
              <p className="font-medium text-slate-800">{selectedTicket.status || "No disponible"}</p>
            </div>
            <div className="col-span-2">
              <p className="text-slate-500">Cliente</p>
              <p className="font-medium text-slate-800">
                {`${selectedTicket.contact?.account?.accountName || ""} ${selectedTicket.contact?.firstName || ""} ${selectedTicket.contact?.lastName || ""}`.trim() || "No disponible"}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/asignar_servicio/${selectedTicket.id}`)}
            className="btn-primary w-full mt-5"
          >
            Asignar Servicio
            <HiArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MainPage;
